// https://github.com/edenspiekermann/a11y-dialog
import Player from '@vimeo/player';
import A11yDialog from 'a11y-dialog';
import axios from 'axios';
import { setupMaps } from './map';
import { attachRecaptcha } from './recaptcha';

/**
 * Setup dialog components
 *
 * Functions can be assigned to the onHideCallback and onShowCallback options.
 *
 * @export
 * @param {*} {
 *   target = '.js-dialog',
 *   bodyClass = 'is-visible--dialog',
 *   onShowCallback = null,
 *   onHideCallback = null,
 * }
 */
export function setupDialogs({
  target = '.js-dialog',
  bodyClass = 'is-visible--dialog',
  onShowCallback = null,
  onHideCallback = null,
} = {}) {
  const dialogs = document.querySelectorAll(target);

  Array.from(dialogs).forEach((el) => {
    setupDialog(el, onShowCallback, onHideCallback, bodyClass);
  });
}

export function setupDialog(
  el = null,
  onShowCallback = null,
  onHideCallback = null,
  bodyClass = 'is-visible--dialog'
) {
  const dialog = new A11yDialog(el);
  const bodyClasses = document.body.classList;

  if (
    !document.body.classList.contains('is-visible--dialog') &&
    el.hasAttribute('data-always-appear')
  ) {
    setTimeout(() => {
      dialog.show();
    }, 300);
  }

  dialog.on('show', () => {
    bodyClasses.add(bodyClass);
    if (typeof onShowCallback === 'function') {
      onShowCallback(dialog);
    }
    // call attachRecpatcha function incase the dialog contains a form requiring recaptcha.
    if (window.recaptchaLoaded) {
      setTimeout(() => attachRecaptcha(), 0);
    }
  });

  dialog.on('hide', () => {
    bodyClasses.remove(bodyClass);
    if (typeof onHideCallback === 'function') {
      onHideCallback(dialog);
    }
  });

  if (el.hasAttribute('data-show-on-load')) {
    dialog.show();
  }

  return dialog;
}

/**
 * Display a video in a dialog
 *
 */
export function setupVideoDialogs() {
  const triggers = document.querySelectorAll('.js-video-dialog');
  const bodyClasses = document.body.classList;

  Array.from(triggers).forEach((trigger) => {
    const url = trigger.dataset.url;
    const title = trigger.dataset.title;

    trigger.addEventListener('click', (ev) => {
      ev.preventDefault();

      const videoPopup = document.createElement('div');
      videoPopup.classList.add('c-dialog', 'c-dialog--video');
      videoPopup.innerHTML = `
            <div class="c-dialog__overlay" tabindex="-1" data-a11y-dialog-hide></div>
            <div class="c-dialog__container" role="dialog" aria-labelledby="dialog-title">
              <button data-a11y-dialog-hide class="c-dialog__close">Close</button>
              <div class="c-dialog__content">
                <h5 class="c-dialog__content__title">${title}</h5>
                <div class="c-dialog__content__video">
                  <iframe
                    class="o-iframe"
                    frameborder="0"
                    allowfullscreen=""
                    allow="autoplay"
                    scrolling="no"
                    src="${url}"
                  >
                  </iframe>
                </div>
              </div>
            </div>`;

      const iframe = videoPopup.querySelector('.o-iframe');
      document.body.appendChild(videoPopup);
      const dialog = new A11yDialog(videoPopup);

      dialog.on('show', (player) => {
        bodyClasses.add('is-visible--dialog');

        if (url.includes('vimeo')) {
          const player = new Player(iframe);
          player.play();
        }
      });

      dialog.show();

      dialog.on('hide', () => {
        dialog.destroy();
        videoPopup.parentNode.removeChild(videoPopup);
        bodyClasses.remove('is-visible--dialog');

        // Force hide all other dialogs too...
        Array.from(document.querySelectorAll('.js-dialog')).forEach(
          (dialogDiv) => {
            dialogDiv.setAttribute('aria-hidden', 'true');
          }
        );
      });
    });
  });
}
/**
 * A dialog containing data fetched from another page
 *
 * @param  url        The page where the content lives
 * @param  contentId  Section of the page you want to fetch
 *
 */
export function setupFetchDialog(url, contentId, dialogClass, override) {
  // initiate an empty popup with spinner, unique ID and all relevant HTML
  const newPopupHTML = document.createElement('div');
  newPopupHTML.classList.add('c-dialog', dialogClass);
  newPopupHTML.id = 'a11y-fetch-dialog';
  newPopupHTML.role = 'dialog';
  newPopupHTML.setAttribute('aria-hidden', true);
  newPopupHTML.innerHTML = `
    <div class="c-dialog__overlay" tabindex="-1" data-a11y-dialog-hide></div>
      <div class="c-dialog__container" role="dialog" aria-labelledby="dialog-title">
        <button data-a11y-dialog-hide class="c-dialog__close">Close</button>
        <div class="c-dialog__content">
          <div class="o-loading-spinner">
            <div class="o-loading-spinner__element"></div>
          </div>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(newPopupHTML);
  const dialog = new A11yDialog(newPopupHTML);
  dialog.on('show', () => {
    document.body.classList.add('is-modal-open');
  });
  dialog.show();
  dialog.on('hide', () => {
    dialog.destroy();
    newPopupHTML.parentNode.removeChild(newPopupHTML);
    document.body.classList.remove('is-modal-open');
    // Force hide all other dialogs too (if there are any)...
    Array.from(document.querySelectorAll('.js-dialog')).forEach((dialogDiv) => {
      dialogDiv.setAttribute('aria-hidden', 'true');
    });
  });
  // get the page content and parse it so we can grab the div
  // if this fails then redirect to the page itself.
  axios
    .get(url)
    .then((response) => {
      // parse the content
      const parser = new DOMParser();
      const sourcePage = parser.parseFromString(response.data, 'text/html');
      // get the content div
      const dialogContent = newPopupHTML.querySelector('.c-dialog__content');
      // remove loading gif
      const loader = dialogContent.querySelector('.o-loading-spinner');
      dialogContent.classList.remove('c-dialog__content--loading');
      dialogContent.removeChild(loader);
      const popupContent = sourcePage.documentElement.querySelector(
        `#${contentId}`
      );
      // Check the content for a redirect link to the fetched page
      const redirectBtn = popupContent.querySelector('.js-redirect a') || false;
      if (redirectBtn) {
        redirectBtn.setAttribute('href', url);
      }

      const appendContent = async () => {
        // add fetch content to dialog
        await dialogContent.appendChild(popupContent);

        // run maps setup again as we've added another map to the page
        setupMaps();
      };

      appendContent();
    })
    .catch((error) => {
      console.log(error); // eslint-disable-line
      // redirect to the page instead
      window.location.href = url;
      return false;
    });
}

export function setupFetchDialogs() {
  Array.from(document.querySelectorAll('.js-fetch-dialog')).forEach((el) => {
    const url = el.getAttribute('href');
    const override = el.getAttribute('data-fetch-override');
    const dialogClass = el.getAttribute('data-fetch-class');
    const { contentId } = el.dataset;
    const minMedia = el.dataset.minMedia || false; // If there's no dialog at mobile
    let media = '';

    if (minMedia) {
      media = `(min-width: ${minMedia})`;
    }

    const mediaQuery = media.length ? window.matchMedia(media) : false;

    el.addEventListener('click', (e) => {
      if (mediaQuery.matches || mediaQuery === false) {
        e.preventDefault();
        setupFetchDialog(url, contentId, dialogClass, override);
      }
    });
  });
}

export default {
  setupDialog,
  setupDialogs,
  setupVideoDialogs,
  setupFetchDialogs,
};
